import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, PieChart, Pie, Cell,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, fmtDate } from "../../lib/api";
import { AlertTriangle } from "lucide-react";
import { Kpi, Th, Td, SectionTitle, Loading, usd, num } from "./shared";

export const KubeClusterOverview = ({ clusterId }) => {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    setData(null);
    api.kube.overview(clusterId).then(setData).catch(console.error);
  }, [clusterId]);

  if (!data) return <Loading label="Loading cluster…" testId="kube-overview-loading" />;
  const k = data.kpis;
  const bp = data.binpacking;
  const sw = data.instance_swaps;

  return (
    <div className="space-y-6" data-testid="kube-cluster-overview">
      {!!data.cluster.unscheduled_pods || data.cluster.agent_version !== data.cluster.latest_agent ? (
        <div className="cs-card p-3.5 border-l-4 border-l-[#92400e] bg-[#fffbeb]" data-testid="kube-health-banner">
          <div className="flex items-start gap-2 text-xs text-[#92400e]">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              {data.cluster.agent_version !== data.cluster.latest_agent && (
                <div>Agent <span className="cs-mono">{data.cluster.agent_version}</span> is behind latest <span className="cs-mono">{data.cluster.latest_agent}</span> — L3 autonomy is blocked until it is upgraded.</div>
              )}
              {!!data.cluster.unscheduled_pods && (
                <div><span className="cs-mono">{data.cluster.unscheduled_pods}</span> unschedulable pods — consolidation is paused while they are pending.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi testId="kube-co-spend" label="Spend / mo" value={usd(k.monthly_spend_usd)} sub={`${k.spot_pct}% on spot`} />
        <Kpi testId="kube-co-nodes" label="Nodes managed" value={`${k.nodes_managed}/${k.nodes_total}`} sub={`${k.nodes_draining} draining · ${num(k.pods)} pods`} />
        <Kpi testId="kube-co-alloc" label="Allocation (req ÷ capacity)" value={`${k.allocation_pct}%`} sub={`utilization ${k.utilization_pct}%`} />
        <Kpi testId="kube-co-binpack-gap" label="Bin-packing gap" value={`${num(k.binpack_gap_cores)} cores`} sub="capacity above requests" accent="text-[#6d28d9]" />
        <Kpi testId="kube-co-rightsize-gap" label="Right-sizing gap" value={`${num(k.rightsizing_gap_cores)} cores`} sub="requests above usage" accent="text-[#00579e]" />
        <Kpi testId="kube-co-headroom" label="Headroom cost / mo" value={usd(data.headroom.monthly_cost_usd)} sub={`${data.headroom.cpu_pct}% policy`} accent="text-[#be185d]" />
      </div>

      <div className="cs-card p-5" data-testid="kube-capacity-chart">
        <SectionTitle hint="Capacity above the request line is the bin-packing opportunity; the request line above usage is the right-sizing opportunity.">
          Capacity vs request vs usage · last 72 hours
        </SectionTitle>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data.capacity_series} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="t" tick={{ fontSize: 9 }} interval={11} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
            <Tooltip formatter={(v, n) => [`${num(v, 1)} cores`, n]} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Area type="monotone" dataKey="spot_cores" name="Spot capacity" stackId="cap" stroke="#15803d" fill="#15803d" fillOpacity={0.18} />
            <Area type="monotone" dataKey="ondemand_cores" name="On-demand capacity" stackId="cap" stroke="#64748b" fill="#64748b" fillOpacity={0.18} />
            <Line type="monotone" dataKey="request_headroom_cores" name="Requests + headroom" stroke="#be185d" strokeWidth={1} dot={false} strokeDasharray="4 3" />
            <Line type="monotone" dataKey="request_cores" name="Workload requests" stroke="#00579e" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="used_cores" name="Actual usage" stroke="#9a3412" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cs-card p-5 lg:col-span-2" data-testid="kube-allocation-series">
          <SectionTitle hint="The main bin-packing health signal — shown as a time series, not only on hover.">Allocation % over time</SectionTitle>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={data.capacity_series} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="t" tick={{ fontSize: 9 }} interval={11} />
              <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v) => `${v}%`} contentStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="allocation_pct" name="Allocation" stroke="#6d28d9" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="cs-card p-5" data-testid="kube-headroom">
          <SectionTitle hint={data.headroom.note}>Headroom composition</SectionTitle>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={110} height={110}>
              <PieChart>
                <Pie data={[
                  { name: "Headroom", value: data.headroom.cpu_cores },
                  { name: "In use", value: Math.max(0, data.headroom.cpu_total_cores - data.headroom.cpu_cores) },
                ]} dataKey="value" innerRadius={34} outerRadius={52} paddingAngle={2}>
                  <Cell fill="#be185d" />
                  <Cell fill="hsl(var(--cs-tint))" />
                </Pie>
                <Tooltip formatter={(v) => `${num(v, 1)} cores`} contentStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-xs space-y-1">
              <div><span className="cs-mono font-semibold">{num(data.headroom.cpu_cores, 1)}</span> cores spare <span className="text-muted-foreground">({data.headroom.cpu_pct}%)</span></div>
              <div><span className="cs-mono font-semibold">{num(data.headroom.mem_gib, 1)}</span> GiB memory spare</div>
              <div className="text-muted-foreground">Billed at <span className="cs-mono text-[#be185d] font-semibold">{usd(data.headroom.monthly_cost_usd)}</span>/mo</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cs-card p-5" data-testid="kube-autoscaling">
          <SectionTitle hint="Every action is also written to the Activity Log with its reason and ChangeSet ID.">Autoscaling activity · last 3 days</SectionTitle>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
            {[["Scale-up events", data.autoscaling.scale_up_events], ["Nodes added", data.autoscaling.scale_up_nodes],
              ["Scale-down events", data.autoscaling.scale_down_events], ["Nodes removed", data.autoscaling.scale_down_nodes],
              ["Reverted to spot", data.autoscaling.revert_to_spot], ["Reverted to lower cost", data.autoscaling.revert_to_lower_cost],
              ["Pod disruptions", data.autoscaling.pod_disruptions], ["Auto-healing events", data.autoscaling.autohealing]].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between border-b border-border/60 py-1">
                <span className="text-muted-foreground">{l}</span>
                <span className="cs-mono font-semibold">{num(v)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="kube-open-binpacking" onClick={() => setDialog("binpack")}
              className="px-3 py-1.5 rounded-sm border border-border text-xs hover:bg-[hsl(var(--cs-tint))] transition-colors">
              Bin-packing detail
            </button>
            <button data-testid="kube-open-swaps" onClick={() => setDialog("swap")}
              className="px-3 py-1.5 rounded-sm border border-border text-xs hover:bg-[hsl(var(--cs-tint))] transition-colors">
              Instance swaps
            </button>
          </div>
        </div>

        <div className="cs-card p-5" data-testid="kube-distribution">
          <SectionTitle hint="Wide instance diversity is what keeps spot reliable — at least 15 candidate shapes per pool.">Instance mix</SectionTitle>
          <div className="overflow-y-auto max-h-[230px]">
            <table className="w-full text-xs">
              <thead><tr><Th>Instance type</Th><Th>Zone</Th><Th>Lifecycle</Th><Th right>Nodes</Th></tr></thead>
              <tbody>
                {data.distribution.map((d) => (
                  <tr key={d.instance_type} className="border-t border-border">
                    <Td mono>{d.instance_type}</Td>
                    <Td mono className="text-muted-foreground">{d.zone}</Td>
                    <Td><span className={d.lifecycle === "spot" ? "text-[#15803d]" : "text-muted-foreground"}>{d.lifecycle}</span></Td>
                    <Td right mono>{d.nodes}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={dialog === "binpack"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-3xl" data-testid="kube-binpacking-dialog">
          <DialogHeader><DialogTitle className="cs-heading text-base">Bin packing · last {bp.window_days} days</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Nodes scaled down" value={num(bp.nodes_scaled_down)} />
            <Kpi label="vCPU saved" value={num(bp.vcpu_saved)} />
            <Kpi label="GiB saved" value={num(bp.gib_saved)} />
            <Kpi label="Pod disruptions" value={num(bp.pod_disruptions)} sub="caused by consolidation" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1.5">By node group</div>
              {bp.by_node_group.map((g) => (
                <div key={g.node_group} className="flex items-center justify-between text-xs border-b border-border/60 py-1">
                  <span>{g.node_group}</span>
                  <span className="cs-mono">{g.share_pct}% <span className="text-muted-foreground">({num(g.nodes)})</span></span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1.5">By lifecycle</div>
              {bp.by_lifecycle.map((g) => (
                <div key={g.lifecycle} className="flex items-center justify-between text-xs border-b border-border/60 py-1">
                  <span>{g.lifecycle}</span><span className="cs-mono">{g.share_pct}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto max-h-[240px] mt-2">
            <table className="w-full text-xs">
              <thead><tr><Th>Node</Th><Th>Node group</Th><Th>Lifecycle</Th><Th right>Pods moved</Th><Th>SLO impact</Th><Th>Scaled down</Th></tr></thead>
              <tbody>
                {bp.nodes.map((n) => (
                  <tr key={n.node_id} className="border-t border-border">
                    <Td mono>{n.node_id}</Td><Td>{n.node_group}</Td><Td>{n.lifecycle}</Td>
                    <Td right mono>{n.pods_moved}</Td>
                    <Td className={n.slo_impact === "none" ? "text-[#15803d]" : "text-[#92400e]"}>{n.slo_impact}</Td>
                    <Td mono className="text-muted-foreground">{fmtDate(n.scaled_down_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "swap"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-3xl" data-testid="kube-swaps-dialog">
          <DialogHeader><DialogTitle className="cs-heading text-base">Instance re-selection · last {sw.window_days} days</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Nodes swapped" value={num(sw.nodes_reverted)} />
            <Kpi label="vCPU hours saved" value={num(sw.vcpu_hours_saved)} />
            <Kpi label="GiB hours saved" value={num(sw.gib_hours_saved)} />
          </div>
          <p className="text-[11px] text-muted-foreground">{sw.note}</p>
          <div className="overflow-x-auto overflow-y-auto max-h-[300px]">
            <table className="w-full text-xs">
              <thead><tr><Th>Node group</Th><Th>From</Th><Th>To</Th><Th right>$/h before</Th><Th right>$/h after</Th><Th right>Hourly %</Th><Th right>$/used core before → after</Th></tr></thead>
              <tbody>
                {sw.swaps.map((s, i) => (
                  <tr key={i} className="border-t border-border">
                    <Td>{s.node_group}</Td>
                    <Td mono>{s.from_instance} <span className="text-muted-foreground">({s.from_cores}c)</span></Td>
                    <Td mono>{s.to_instance} <span className="text-muted-foreground">({s.to_cores}c)</span></Td>
                    <Td right mono>${s.from_hourly_usd}</Td>
                    <Td right mono>${s.to_hourly_usd}</Td>
                    <Td right mono className="text-[#15803d]">{s.hourly_saving_pct}%</Td>
                    <Td right mono>${s.cost_per_used_core_before} → ${s.cost_per_used_core_after}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KubeClusterOverview;
